import { useId } from 'react'
import { Settings2 } from 'lucide-react'

import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AppraisalField } from '@/components/deals/appraisal/AppraisalField'
import { AppraisalSection } from '@/components/deals/appraisal/AppraisalSection'

const STRATEGY_OPTIONS = [
  'BRRR / Flip',
  'HMO',
  'Commercial Conversion',
  'Commercial / Mixed-Use',
]

const TARGET_METRIC_OPTIONS = ['ROCE']

export function StrategyTargetingPanel({
  currentStrategy,
  targetMetric,
  targetReturn,
  onCurrentStrategyChange,
  onTargetMetricChange,
  onTargetReturnChange,
}) {
  const selectedMetric = targetMetric ?? 'ROCE'

  return (
    <AppraisalSection title="Strategy & Targeting" icon={Settings2}>
      <div className="grid gap-5 md:grid-cols-2">
        <div className="space-y-1.5 md:col-span-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Selected Strategy
            </span>
            <span className="rounded bg-primary/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-primary">
              Pre-populated
            </span>
          </div>
          <LabeledSelect
            label="Selected Strategy"
            value={currentStrategy ?? 'BRRR / Flip'}
            placeholder="Select strategy"
            options={STRATEGY_OPTIONS}
            onValueChange={(value) => onCurrentStrategyChange?.(value)}
            isLabelHidden
          />
        </div>
        <LabeledSelect
          label="Target Metric"
          value={selectedMetric}
          placeholder="Select target metric"
          options={TARGET_METRIC_OPTIONS}
          onValueChange={(value) => onTargetMetricChange?.(value)}
        />
        <AppraisalField
          label="Target Return (%)"
          value={targetReturn ?? ''}
          suffix="%"
          inputMode="decimal"
          onChange={(event) => onTargetReturnChange?.(event.target.value)}
        />
      </div>
    </AppraisalSection>
  )
}

function LabeledSelect({ label, value, placeholder, options, onValueChange, isLabelHidden = false }) {
  const selectId = useId()

  return (
    <div className="space-y-1.5">
      {isLabelHidden ? (
        <span id={`${selectId}-label`} className="sr-only">
          {label}
        </span>
      ) : (
        <Label
          htmlFor={selectId}
          className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
        >
          {label}
        </Label>
      )}
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          id={selectId}
          className="w-full"
          aria-label={isLabelHidden ? undefined : label}
          aria-labelledby={isLabelHidden ? `${selectId}-label` : undefined}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
